import { useCallback, useEffect, useState } from "react";

import { useSession } from "../../session/SessionContext.js";

/**
 * Aggregate reporting.
 *
 * Two decisions shape this screen.
 *
 * **Medians sit beside means.** Support durations are long-tailed — one conversation
 * left open over a weekend moves the mean by minutes and the median not at all — and
 * a team reading only the average would conclude something untrue about a normal day.
 *
 * **Proxies say they are proxies, and gaps say they are gaps.** Two of the metrics
 * the brief asks for cannot be computed from anything this platform records, and the
 * API says so in its response. Rendering that as text is better than an empty panel
 * that looks like a bug, and far better than a number nobody can check.
 */

interface Analytics {
  range: { from: string; to: string };
  conversations: {
    total: number;
    byStatus: Record<string, number>;
    byChannel: Record<string, number>;
    escalated: number;
    escalationsByReason: Record<string, number>;
    closedWithoutEscalation: number;
    humanResolution: {
      samples: number;
      averageSeconds: number | null;
      medianSeconds: number | null;
    };
    ratings: { count: number; average: number | null; distribution: Record<string, number> };
    daily: { date: string; count: number }[];
  };
  assistant: {
    replies: number;
    averageLatencyMs: number | null;
    medianLatencyMs: number | null;
    averageConfidence: number | null;
    refusalsByReason: Record<string, number>;
    topCitedDocuments: { documentId: string; citations: number; title?: string }[];
  };
  rates: { escalation: number | null; handledWithoutAPerson: number | null };
  notComputed: { metric: string; reason: string }[];
}

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
] as const;

function percent(value: number | null): string {
  return value === null ? "—" : `${(value * 100).toFixed(1)}%`;
}

function duration(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  if (seconds < 3_600) return `${(seconds / 60).toFixed(1)} min`;

  return `${(seconds / 3_600).toFixed(1)} h`;
}

function millis(value: number | null): string {
  return value === null
    ? "—"
    : value < 1_000
      ? `${value.toFixed(0)} ms`
      : `${(value / 1_000).toFixed(1)} s`;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="stat">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {hint !== undefined && <div className="hint">{hint}</div>}
    </div>
  );
}

/** Counts as bars, sorted. Enough to read a distribution; no charting library. */
function Bars({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data).sort((left, right) => right[1] - left[1]);
  const max = Math.max(1, ...entries.map(([, count]) => count));

  if (entries.length === 0) return <p className="muted small">Nothing in this period.</p>;

  return (
    <ul className="bars">
      {entries.map(([key, count]) => (
        <li key={key}>
          <span className="bar-label">{key.replace(/_/gu, " ")}</span>
          <span className="bar" style={{ width: `${String((count / max) * 100)}%` }} />
          <span className="bar-count">{count}</span>
        </li>
      ))}
    </ul>
  );
}

export function AnalyticsPanel() {
  const { api } = useSession();

  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<Analytics | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  const load = useCallback(async () => {
    setStatus("loading");

    try {
      const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

      setData(await api.get<Analytics>(`/support/analytics?from=${encodeURIComponent(from)}`));
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [api, days]);

  useEffect(() => {
    void load();
  }, [load]);

  if (status === "error") return <p className="error">Analytics could not be loaded.</p>;
  if (data === null) return <p className="muted">Loading…</p>;

  const { conversations, assistant, rates } = data;

  return (
    <div className="admin-form">
      <div className="row">
        {RANGES.map((option) => (
          <button
            key={option.days}
            className={days === option.days ? "primary" : ""}
            onClick={() => {
              setDays(option.days);
            }}
          >
            {option.label}
          </button>
        ))}
        {status === "loading" && <span className="small muted">Refreshing…</span>}
      </div>

      <section className="card">
        <h2>Volume</h2>
        <div className="stats">
          <Stat label="Conversations" value={String(conversations.total)} />
          <Stat label="Assistant replies" value={String(assistant.replies)} />
          <Stat
            label="Escalation rate"
            value={percent(rates.escalation)}
            hint="Reached a person at any point"
          />
          <Stat
            label="Handled without a person"
            value={percent(rates.handledWithoutAPerson)}
            hint="Closed without ever escalating. Not the same as accuracy — see below."
          />
        </div>
      </section>

      <section className="card">
        <h2>Speed</h2>
        <div className="stats">
          <Stat label="AI reply, median" value={millis(assistant.medianLatencyMs)} />
          <Stat label="AI reply, mean" value={millis(assistant.averageLatencyMs)} />
          <Stat
            label="Human resolution, median"
            value={duration(conversations.humanResolution.medianSeconds)}
            hint={`${String(conversations.humanResolution.samples)} conversations, from first agent joining to close`}
          />
          <Stat
            label="Human resolution, mean"
            value={duration(conversations.humanResolution.averageSeconds)}
          />
        </div>
      </section>

      <section className="card">
        <h2>Why conversations reached a person</h2>
        <Bars data={conversations.escalationsByReason} />
      </section>

      <section className="card">
        <h2>When the assistant declined</h2>
        <p className="hint">
          Worth watching in both directions. A rise in <em>medical</em> may mean more dosing
          questions — or that a guardrail has started catching ordinary shop questions.
        </p>
        <Bars data={assistant.refusalsByReason} />
      </section>

      <section className="card">
        <h2>What customers needed to know</h2>
        <p className="hint">
          The knowledge articles cited most often. This is a stand-in for &ldquo;most frequent
          questions&rdquo;: it is computed from what the assistant actually used, rather than by
          guessing at the meaning of message text.
        </p>

        {assistant.topCitedDocuments.length === 0 ? (
          <p className="muted small">No citations in this period.</p>
        ) : (
          <ul className="bars">
            {assistant.topCitedDocuments.map((entry) => (
              <li key={entry.documentId}>
                {/* Falls back to the id when the corpus could not be reached. Ugly,
                    but honest — and the count beside it is still correct. */}
                <span className="bar-label">{entry.title ?? entry.documentId}</span>
                <span className="bar-count">{entry.citations}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Satisfaction</h2>
        <div className="stats">
          <Stat
            label="Average rating"
            value={
              conversations.ratings.average === null
                ? "—"
                : `${conversations.ratings.average.toFixed(2)} / 5`
            }
            hint={`${String(conversations.ratings.count)} ratings`}
          />
        </div>
        <Bars data={conversations.ratings.distribution} />
      </section>

      <section className="card">
        <h2>Not measured</h2>
        <p className="hint">
          These were asked for and are deliberately absent rather than estimated.
        </p>
        <ul>
          {data.notComputed.map((entry) => (
            <li key={entry.metric} className="small">
              <strong>{entry.metric}</strong> — {entry.reason}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
